<template>
  <div class="panel-container">
    <div class="header-container">
      <div class="title-container">
        <span class="title-text">现货收益明细</span>
        <span class="title-sub" v-if="rows.length">共 {{ rows.length }} 条</span>
      </div>
      <div class="btns-container">
        <slot name="actions"></slot>
      </div>
    </div>

    <el-alert
      v-if="summary.incompleteRtCount > 0"
      class="incomplete-alert"
      type="warning"
      :closable="false"
      show-icon
      :title="`区间内有 ${summary.incompleteRtCount} 个时段缺实时价，实时收益合计值偏小`"
    />

    <div class="main-container">
      <el-table
        :data="rows"
        v-loading="loading"
        max-height="440"
        size="small"
        border
        show-summary
        :summary-method="summaryMethod"
        row-key="_key"
      >
        <el-table-column prop="settleDate" label="日期" width="110" fixed />
        <el-table-column prop="timeSlot" label="时段" width="90" v-if="granularity !== 'day'">
          <template #default="{ row }">{{ row.timeSlot || '--' }}</template>
        </el-table-column>
        <el-table-column label="电表" width="150" v-if="showMeterColumn">
          <template #default="{ row }">{{ meterName(row.uuid) }}</template>
        </el-table-column>

        <el-table-column prop="chargeEnergy" label="充电量(kWh)" width="120" align="right">
          <template #default="{ row }">{{ num(row.chargeEnergy) }}</template>
        </el-table-column>
        <el-table-column prop="dischargeEnergy" label="放电量(kWh)" width="120" align="right">
          <template #default="{ row }">{{ num(row.dischargeEnergy) }}</template>
        </el-table-column>
        <!-- 净电量列按业务要求隐藏，字段仍由接口返回，需要时恢复本列即可 -->

        <el-table-column label="实时收益(元)" width="130" align="right">
          <template #default="{ row }">
            <span :class="{ 'is-blank': isBlankVal(row.realtimeIncome), 'is-negative': isNegative(row.realtimeIncome) }">
              {{ num(row.realtimeIncome) }}
            </span>
            <el-tooltip
              v-if="row.realtimeComplete === false"
              :content="`该时段 ${row._missingRt}/${row.slotTotal} 格缺价，汇总值偏小`"
              placement="top"
              :hide-after="0"
            >
              <span class="warn-badge">⚠</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="日前收益(元)" width="130" align="right">
          <template #default="{ row }">
            <span :class="{ 'is-blank': isBlankVal(row.dayaheadIncome), 'is-negative': isNegative(row.dayaheadIncome) }">
              {{ num(row.dayaheadIncome) }}
            </span>
            <el-tooltip
              v-if="row.dayaheadComplete === false"
              :content="`该时段 ${row._missingDa}/${row.slotTotal} 格缺日前价，汇总值偏小`"
              placement="top"
              :hide-after="0"
            >
              <span class="warn-badge">⚠</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column label="实时均价(元/MWh)" width="150" align="right">
          <template #default="{ row }">{{ num(row.realtimeAvgPrice) }}</template>
        </el-table-column>
        <el-table-column label="日前均价(元/MWh)" width="150" align="right">
          <template #default="{ row }">{{ num(row.dayaheadAvgPrice) }}</template>
        </el-table-column>

        <el-table-column label="放电收入参考(元)" width="150" align="right">
          <template #header>
            <span>放电收入参考(元)</span>
            <el-tooltip content="重算值，与实时收益可能差几分，仅供参考" placement="top" :hide-after="0">
              <span class="head-tip">?</span>
            </el-tooltip>
          </template>
          <template #default="{ row }">{{ num(row.dischargeIncomeRef) }}</template>
        </el-table-column>
        <el-table-column label="充电成本参考(元)" width="150" align="right">
          <template #header>
            <span>充电成本参考(元)</span>
            <el-tooltip content="重算值，与实时收益可能差几分，仅供参考" placement="top" :hide-after="0">
              <span class="head-tip">?</span>
            </el-tooltip>
          </template>
          <template #default="{ row }">{{ num(row.chargeCostRef) }}</template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { fmtIncome } from '@/utils/fortmatter.js'
import { isBlank, sumSkipBlank } from '../../../../composables/useSpotSettlement.js'

defineOptions({ name: 'SpotDetailTablePanel' })

const props = defineProps({
  rows: { type: Array, default: () => [] },
  granularity: { type: String, default: '15min' },
  loading: { type: Boolean, default: false },
  summary: { type: Object, default: () => ({}) },
  /** uuid -> meterName，用于把电表列渲染成名称 */
  meterMap: { type: Object, default: () => ({}) },
})

/** 接口 byMeter 时同一时段会出现多行，此时才需要电表列 */
const showMeterColumn = computed(() => props.rows.some((r) => r.uuid))

function meterName(uuid) {
  if (!uuid) return '整站汇总'
  return props.meterMap?.[uuid] || uuid
}

/** 缺价一律显示 --，绝不退化成 0 */
function num(value) {
  return fmtIncome(value)
}

function isBlankVal(value) {
  return isBlank(value)
}

function isNegative(value) {
  if (isBlank(value)) return false
  return Number(value) < 0
}

/** 合计行：跳过缺价，均价类不做合计 */
function summaryMethod({ columns }) {
  const list = props.rows
  const sumKeyByLabel = {
    '充电量(kWh)': 'chargeEnergy',
    '放电量(kWh)': 'dischargeEnergy',
    '实时收益(元)': 'realtimeIncome',
    '日前收益(元)': 'dayaheadIncome',
    '放电收入参考(元)': 'dischargeIncomeRef',
    '充电成本参考(元)': 'chargeCostRef',
  }
  return columns.map((col, index) => {
    if (index === 0) return '合计'
    const key = sumKeyByLabel[col.label]
    if (!key) return ''
    const total = sumSkipBlank(list, key)
    return total === null ? '--' : fmtIncome(total)
  })
}
</script>

<style lang="scss" scoped>
.panel-container {
  width: 100%;
  height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    .title-container {
      display: flex;
      align-items: baseline;
      gap: 8px;

      .title-text {
        color: #2e2e2e;
        font-family: 'HarmonyOS Sans SC';
        font-size: 16px;
      }

      .title-sub {
        color: #999;
        font-family: 'HarmonyOS Sans SC';
        font-size: 12px;
      }
    }
  }

  .incomplete-alert {
    margin-bottom: 12px;
  }

  .main-container {
    flex: 1;
    min-height: 320px;
  }
}

.is-blank {
  color: #bbb;
}

.is-negative {
  color: #f56c6c;
}

.warn-badge {
  margin-left: 4px;
  color: #e6a23c;
  cursor: pointer;
}

.head-tip {
  display: inline-block;
  margin-left: 4px;
  width: 14px;
  height: 14px;
  line-height: 14px;
  text-align: center;
  border-radius: 50%;
  background: #eef1f6;
  color: #909399;
  font-size: 11px;
  cursor: pointer;
}
</style>
